import { useState } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import PageHero from "@/components/sections/PageHero";
import CTASection from "@/components/sections/CTASection";
import { Reveal } from "@/components/motion/Reveal";
import { publishedArticles, insightCategories } from "@/content/insights";
import { ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";

export default function Insights() {
  const [active, setActive] = useState("All");
  const filtered =
    active === "All"
      ? publishedArticles
      : publishedArticles.filter((a) => a.category === active);

  return (
    <>
      <SEO
        title="Insights for Trade Business Growth | Green Circle Group"
        description="Practical articles on local visibility, lead generation, customer follow-up, reviews, automation and business growth for Australian trade businesses."
        path="/insights"
      />
      <PageHero
        eyebrow="Insights"
        heading="Practical growth ideas for busy trade businesses."
        copy="Short, useful articles on getting found, generating enquiries and following up consistently—written for owners, not marketers."
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Insights", to: "/insights" },
        ]}
      />

      <section className="bg-soft py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          {/* categories */}
          <div className="flex flex-wrap gap-2.5">
            {["All", ...insightCategories].map((cat) => (
              <button
                key={cat}
                onClick={() => setActive(cat)}
                data-testid={`insight-filter-${cat.replace(/\s+/g, "-").toLowerCase()}`}
                className={cn(
                  "rounded-full px-4 py-2 text-sm font-semibold transition-colors border",
                  active === cat
                    ? "bg-forest text-white border-forest"
                    : "bg-white text-mid-neutral border-border-neutral hover:border-mint"
                )}
              >
                {cat}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="mt-14 rounded-[1.5rem] bg-white border border-border-neutral p-10 text-center text-mid-neutral">
              No articles in this category yet. Check back soon.
            </div>
          ) : (
            <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((a, i) => (
                <Reveal key={a.slug} delay={(i % 3) * 0.06}>
                  <Link
                    to={`/insights/${a.slug}`}
                    data-testid={`article-card-${a.slug}`}
                    className="group flex flex-col h-full rounded-[1.5rem] bg-white border border-border-neutral p-7 hover:border-mint transition-colors"
                  >
                    <span className="text-xs font-bold uppercase tracking-[0.15em] text-mint">
                      {a.category}
                    </span>
                    <h2 className="mt-3 text-xl font-extrabold text-forest leading-snug">
                      {a.title}
                    </h2>
                    <p className="mt-3 text-mid-neutral leading-relaxed flex-1">
                      {a.excerpt}
                    </p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-forest">
                      Read article
                      <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <CTASection />
    </>
  );
}
